/**
 * Login — email/password authentication screen.
 *
 * Uses a direct REST call to Supabase's /auth/v1/token endpoint instead of
 * the JS client to avoid intermittent PKCE-related hangs on initial load.
 * On success the session is saved to sessionStorage so AuthContext.init()
 * can hydrate the user without a round-trip to the Supabase client.
 */
import React, { useState } from 'react';
import { supabase } from '../../config/supabase';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import logo from '../../components/images/logo.png';
import characterImage from '../../components/images/onboarding_image.png';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { email, password } = formData;

      /*
       * Direct REST call bypasses the Supabase JS client, which can hang
       * on first load due to PKCE flow initialisation in some environments.
       */
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`
        },
        body: JSON.stringify({ email: email.trim(), password })
      });

      const responseData = await response.json();

      if (!response.ok) {
        if (response.status === 400) {
          if (responseData.error_description?.includes('Invalid login credentials') ||
              responseData.msg?.includes('Invalid login credentials')) {
            setError('Invalid email or password');
          } else {
            setError(responseData.error_description || responseData.msg || 'Login failed');
          }
        } else if (response.status === 422) {
          setError('Please check your email and password');
        } else {
          setError(responseData.error_description || responseData.msg || 'Login failed');
        }
        setIsLoading(false);
        return;
      }

      if (!responseData.user || !responseData.access_token) {
        setError('Login failed. Please try again.');
        setIsLoading(false);
        return;
      }

      // Persist session manually so AuthContext.init() can read it on next render
      const sessionData = {
        access_token: responseData.access_token,
        refresh_token: responseData.refresh_token,
        user: responseData.user,
        expires_at: responseData.expires_at
      };
      sessionStorage.setItem('supabase-auth-token', JSON.stringify(sessionData));

      // Sync the Supabase client in the background — navigation doesn't depend on it
      supabase.auth.setSession({
        access_token: responseData.access_token,
        refresh_token: responseData.refresh_token
      }).catch(() => { /* non-critical */ });

      // Determine the correct landing page from the user's role so that roles
      // without `overview:view` (super_admin, system_admin) don't land on /dashboard.
      let landingPath = '/dashboard';
      try {
        const profileRes = await fetch(
          `${supabaseUrl}/rest/v1/profiles?id=eq.${responseData.user.id}&select=role`,
          {
            headers: {
              'apikey': supabaseKey,
              'Authorization': `Bearer ${responseData.access_token}`,
            }
          }
        );
        if (profileRes.ok) {
          const [profileData] = await profileRes.json();
          const role = profileData?.role?.toLowerCase();
          if (role === 'super_admin') landingPath = '/admin/tenants';
          else if (role === 'system_admin') landingPath = '/user-management';
        }
      } catch (e) {
        // Non-critical — fall through to /dashboard
      }

      setIsLoading(false);
      navigate(landingPath, { replace: true });

      // Fallback in case React Router navigation is blocked
      setTimeout(() => {
        if (window.location.pathname !== landingPath) {
          window.location.href = landingPath;
        }
      }, 500);

    } catch (err) {
      console.error('Login error:', err.message);
      if (err.message?.includes('Failed to fetch') || err.name === 'TypeError') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(`Login failed: ${err.message}`);
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="login-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="TalentScope360" />
        </div>

        <div className="login-content">
          <h1 className="login-title">Login</h1>
          <p className="login-subtitle">Welcome back! Please enter your details.</p>

          <form className="login-form" onSubmit={handleSubmit}>
            {error && (
              <div className="login-error" style={{ 
                color: '#dc2626', 
                backgroundColor: '#fee2e2', 
                padding: '12px', 
                borderRadius: '6px', 
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            <input
              type="email"
              name="email"
              placeholder="Email"
              className="login-input"
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />

            <div className="login-password-field">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                className="login-input"
                value={formData.password}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label="Toggle password visibility"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {showPassword ? (
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </>
                  ) : (
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </>
                  )}
                </svg>
              </button>
            </div>

            <div className="login-options">
              <label className="login-remember">
                <input type="checkbox" disabled={isLoading} />
                <span>Remember me</span>
              </label>
              <button type="button" className="login-forgot" onClick={() => {}}>Forgot password?</button>
            </div>

            <button type="submit" className="login-button" disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="login-footer">
            <p>Don't have an account? <a href="/sign-up">Sign up</a></p>
          </div>
        </div>
      </div>

      <div className="login-right">
        <div className="login-right-content">
          <div className="login-character">
            <img src={characterImage} alt="Character" />
          </div>
          <h2 className="login-right-title">Streamline your Talent Management.</h2>
          <p className="login-right-text">
            Transform your workforce into a competitive advantage with TalentScope360.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;